import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Flex } from '#app/components/layout/flex.tsx'
import { Avatar } from '#app/components/media/avatar.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Avatar',
}

const component: componentProps = {
	name: 'Avatar',
	shortName: 'Avatar',
	fileName: 'avatar',
	category: 'media',
	description: [`The Avatar component is a thumbnail representation of an entity, such as a user or an organization.`],
	features: [
		`Requires an alt attribute for an accessible description.`,
		`Renders an avatar icon if no src or text prop is provided`,
		`Accepts a text prop to display a fallback text.`,
		`Accepts a src attribute defining the image URL.`,
		`Fallbacks to icon or text if image is not provided or fails to load.`,
		`Accepts a size prop to set the icon size.`,
		`Smooth image fade-in to avoid content flashing.`,
	],
	usage: `<Flex items="end" gap="5" className="my-4">
	<Avatar size="sm" alt="Avatar" />
	<Avatar size="md" alt="Avatar" />
	<Avatar size="lg" alt="Avatar" />
</Flex>
<Flex items="end" gap="5" className="my-4">
	<Avatar size="sm" alt="Avatar" text="Ney" />
	<Avatar size="md" alt="Avatar" text="Jake" />
	<Avatar size="lg" alt="Avatar" text="MQ" />
</Flex>
<Flex items="end" gap="5" className="my-4">
	<Avatar size="sm" alt="Avatar" text="Ney" src="/img/neytiri.webp" />
	<Avatar size="md" alt="Avatar" text="Jake" src="/img/jake.webp" />
	<Avatar size="lg" alt="Avatar" text="MQ" src="/img/miles-quaritch.png" />
</Flex>`,
	parts: [],
	styling: `.avatar {
	@apply bg-muted-400 relative flex items-center justify-center overflow-hidden rounded-full text-white;

	/* Sizes */
	&.sm {
		@apply h-fluid-12 w-fluid-12;
		> span {
			@apply text-xs;
		}
	}
	&.md {
		@apply h-fluid-14 w-fluid-14;
		> span {
			@apply text-sm;
		}
	}
	&.lg {
		@apply h-fluid-15 w-fluid-15;
		> span {
			@apply text-md;
		}
	}

	& > img {
		@apply absolute min-h-full min-w-full object-cover;
	}

	& > span {
		@apply absolute flex items-center justify-center;
	}
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const Route = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<Flex items="end" gap="5" className="my-4">
				<Avatar size="sm" alt="Avatar" />
				<Avatar size="md" alt="Avatar" />
				<Avatar size="lg" alt="Avatar" />
			</Flex>
			<Flex items="end" gap="5" className="my-4">
				<Avatar size="sm" alt="Avatar" text="Ney" />
				<Avatar size="md" alt="Avatar" text="Jake" />
				<Avatar size="lg" alt="Avatar" text="MQ" />
			</Flex>
			<Flex items="end" gap="5" className="my-4">
				<Avatar size="sm" alt="Avatar" text="Ney" src="/img/neytiri.webp" />
				<Avatar size="md" alt="Avatar" text="Jake" src="/img/jake.webp" />
				<Avatar size="lg" alt="Avatar" text="MQ" src="/img/miles-quaritch.png" />
			</Flex>

			<Features features={features} />
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default Route
